import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  InputBase,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Button,
} from '@mui/material';
import usePagination from '@mui/material/usePagination';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '../../../../assets/Admin/RequestImages/filter-icon.svg';
import { axiosInstance } from '../../../../utils/tokenUtils';
import { toast } from 'react-hot-toast';
import InstructorTable from '../InstructorTable/InstructorTable';
import AddInstructorModal from '../AddInstructorModal/AddInstructorModal';
import './OrganizationInstructor.css';
import InstructorIcon from '../../../../assets/Admin/Organisation/instructor.svg';
import { useNavigate } from 'react-router-dom';
import '../../SkeletonLoader/SkeletonLoader.css';

const OrganizationInstructor = ({ organizationId, canManageOrganisations = false }) => {
  const [instructors, setInstructors] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Removed selectedInstructorIds and related state
  const [showAddInstructorModal, setShowAddInstructorModal] = useState(false);

  // Country list for filter
  const [countryList, setCountryList] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  // Applied filter states (sent to API)
  const [instructorTypeFilter, setInstructorTypeFilter] = useState('All');
  const [countryFilter, setCountryFilter] = useState('All');
  const [genderFilter, setGenderFilter] = useState('All');
  // Pending filter states (for filter panel)
  const [pendingInstructorTypeFilter, setPendingInstructorTypeFilter] = useState('All');
  const [pendingCountryFilter, setPendingCountryFilter] = useState('All');
  const [pendingGenderFilter, setPendingGenderFilter] = useState('All');
  const [showFilters, setShowFilters] = useState(false);

  // Sorting states
  const [totalSessionsSort, setTotalSessionsSort] = useState('desc');
  const [ratingSort, setRatingSort] = useState('desc');

  // Ref for filter panel
  const filterPanelRef = useRef(null);

  // Fetch instructors for the organization
  const fetchOrganizationInstructors = useCallback(async () => {
    if (!organizationId) {
      setError('Organization ID is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = { page, page_size: rowsPerPage };

      // Add search query if not empty
      if (searchQuery.trim()) {
        params.search_query = searchQuery.trim();
      }

      // Add filters if not 'All'
      if (instructorTypeFilter !== 'All') {
        params.teacher_type = instructorTypeFilter.toLowerCase();
      }

      if (countryFilter !== 'All') {
        params.country = countryFilter;
      }

      if (genderFilter !== 'All') {
        params.gender = genderFilter;
      }

      // Add sorting parameters
      if (totalSessionsSort !== 'desc') {
        params.total_sessions_sort = totalSessionsSort;
      }

      if (ratingSort !== 'desc') {
        params.rating_sort = ratingSort;
      }

      // Debug logging
      console.log('API Request Params:', params);
      console.log('Search Query:', searchQuery.trim());

      const response = await axiosInstance.get(`/admin/organisation/teachers/${organizationId}`, {
        params,
      });

      // Debug logging
      console.log('API Response:', response.data);
      console.log('Results count:', response.data.results?.length);
      console.log('Total count:', response.data.count);

      // Validate search results - if we have a search query but the API returns all data,
      // this suggests the search is not working on the backend
      let finalResults = response.data.results || [];
      let finalCount = response.data.count || 0;

      if (searchQuery.trim() && finalCount > 0) {
        // If we have a search query and results, validate that the results actually match
        // This is a fallback check in case the API doesn't properly filter
        const searchLower = searchQuery.trim().toLowerCase();
        const filteredResults = finalResults.filter(instructor => {
          const fullName =
            `${instructor.first_name || ''} ${instructor.last_name || ''}`.toLowerCase();
          const email = (instructor.email || '').toLowerCase();
          const subjects = Array.isArray(instructor.subjects)
            ? instructor.subjects
                .map(s => (typeof s === 'object' && s?.title ? s.title : s))
                .join(' ')
                .toLowerCase()
            : '';

          return (
            fullName.includes(searchLower) ||
            email.includes(searchLower) ||
            subjects.includes(searchLower)
          );
        });

        // If client-side filtering reduces results significantly, use that instead
        if (filteredResults.length < finalResults.length * 0.8) {
          console.log('API search may not be working properly, using client-side fallback');
          finalResults = filteredResults;
          finalCount = filteredResults.length;
        }
      }

      // Transform the API response to match the expected format for InstructorTable
      const transformedInstructors = finalResults.map(instructor => ({
        id: instructor.id,
        first_name: instructor.first_name,
        last_name: instructor.last_name,
        profile_image: instructor.profile_image_url,
        instructor_type: instructor.teacher_type === 'academic' ? 'Academic' : 'Creative',
        country: instructor.country,
        country_code: instructor.country_code || 'US', // Default fallback
        subjects: instructor.subjects,
        total_sessions: instructor.total_sessions,
        rating: instructor.rating,
        allow_one_on_one_session: instructor.allowed_session_types?.one_on_one === 'enabled',
        allow_group_session: instructor.allowed_session_types?.group === 'enabled',
        allow_prime_session: instructor.allowed_session_types?.prime === 'enabled',
        is_active: instructor.approval_status === 'approved',
        gender: instructor.gender,
        email: instructor.email,
        phone_no: instructor.phone_no,
        current_location: instructor.current_location,
        highest_qualification: instructor.highest_qualification,
        degree_name: instructor.degree_name,
        institute: instructor.institute,
        level_of_teaching: instructor.level_of_teaching,
        teaching_lang: instructor.teaching_lang,
        years_of_experience: instructor.years_of_experience,
        onboarded_date: instructor.registration_date,
      }));

      setInstructors(transformedInstructors);
      setTotalCount(finalCount || 0);
    } catch (error) {
      console.error('Error fetching organization instructors:', error);

      if (error.response) {
        const status = error.response.status;
        if (status === 404) {
          setError('Organization not found');
        } else if (status === 401 || status === 403) {
          setError('You are not authorized to view this organization');
        } else {
          setError('Failed to load organization instructors');
        }
      } else if (error.request) {
        setError('Network error. Please check your internet connection');
      } else {
        setError('An unexpected error occurred');
      }

      setInstructors([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  }, [
    organizationId,
    searchQuery,
    instructorTypeFilter,
    countryFilter,
    genderFilter,
    totalSessionsSort,
    ratingSort,
    page,
    rowsPerPage,
  ]);

  // Fetch all countries for the Country filter
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setCountriesLoading(true);
        const { data } = await axiosInstance.get('/auth/countries');
        // Expecting an array of { id, code, name }
        setCountryList(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Error fetching countries:', e);
        setCountryList([]);
      } finally {
        setCountriesLoading(false);
      }
    };
    fetchCountries();
  }, []);

  // Fetch instructors when component mounts or organizationId changes
  useEffect(() => {
    fetchOrganizationInstructors();
  }, [fetchOrganizationInstructors]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim() !== '') {
        // Reset to first page when searching
        setPage(1);
        setSearching(true);
        // Trigger API call with search
        fetchOrganizationInstructors();
      } else if (searchQuery === '') {
        // If search is cleared, reset to first page and fetch all
        setPage(1);
        setSearching(false);
        fetchOrganizationInstructors();
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Handle click outside filter modal
  useEffect(() => {
    const handleClickOutside = event => {
      if (showFilters && filterPanelRef.current && !filterPanelRef.current.contains(event.target)) {
        // Check if the click is on a Material-UI Select dropdown (which renders in a portal)
        const isSelectDropdown =
          event.target.closest('.MuiPopover-root') ||
          event.target.closest('.MuiPaper-root') ||
          event.target.closest('.MuiMenu-root');

        if (!isSelectDropdown) {
          setShowFilters(false);
        }
      }
    };

    if (showFilters) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilters]);

  // Removed handleSelectOne, handleSelectAll, handleRemoveSelected

  // Add Instructor Modal handlers
  const handleAddInstructor = () => {
    if (!canManageOrganisations) {
      toast.dismiss();
      toast.error("You don't have permission to add instructors");
      return;
    }
    navigate(`/admin/organisation/add-instructor/${organizationId}`);
  };

  const handleCloseAddInstructorModal = () => {
    setShowAddInstructorModal(false);
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscapeKey = event => {
      if (event.key === 'Escape' && showAddInstructorModal) {
        handleCloseAddInstructorModal();
      }
      if (event.key === 'Escape' && showFilters) {
        setShowFilters(false);
      }
    };

    if (showAddInstructorModal || showFilters) {
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [showAddInstructorModal, showFilters]);

  // Filter handlers
  const handlePendingFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'instructorType':
        setPendingInstructorTypeFilter(value);
        break;
      case 'country':
        setPendingCountryFilter(value);
        break;
      case 'gender':
        setPendingGenderFilter(value);
        break;
      default:
        break;
    }
  };

  const clearAllPendingFilters = () => {
    setPendingInstructorTypeFilter('All');
    setPendingCountryFilter('All');
    setPendingGenderFilter('All');
  };

  const handleOpenFilters = () => {
    setPendingInstructorTypeFilter(instructorTypeFilter);
    setPendingCountryFilter(countryFilter);
    setPendingGenderFilter(genderFilter);
    setShowFilters(true);
  };

  const handleApplyFilters = () => {
    setInstructorTypeFilter(pendingInstructorTypeFilter);
    setCountryFilter(pendingCountryFilter);
    setGenderFilter(pendingGenderFilter);
    setPage(1);
    setShowFilters(false);
  };

  // Pagination handlers
  const handleRowsPerPageChange = event => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };
  const handlePageChange = (event, value) => {
    setPage(value);
  };
  const handleTotalSessionsSort = () => {
    const newSort = totalSessionsSort === 'desc' ? 'asc' : 'desc';
    setTotalSessionsSort(newSort);
  };

  const handleRatingSort = () => {
    const newSort = ratingSort === 'desc' ? 'asc' : 'desc';
    setRatingSort(newSort);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  const { items: paginationItems } = usePagination({
    count: totalPages,
    page,
    onChange: handlePageChange,
  });

  // Determine if any filters or search are applied
  const hasAppliedFiltersOrSearch =
    searchQuery.trim() !== '' ||
    instructorTypeFilter !== 'All' ||
    countryFilter !== 'All' ||
    genderFilter !== 'All';

  return (
    <Box className="org-instructor-main-container">
      {error && (
        <Box className="org-instructor-error-container" sx={{ p: 1 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      {(hasAppliedFiltersOrSearch || (instructors && instructors.length > 0)) && (
        <Box className="org-instructor-search-controls-section">
          <Box className="org-instructor-search-section">
            <Box className="org-instructor-search-bar">
              <SearchIcon className="org-instructor-search-icon" />
              <InputBase
                placeholder="Search by Name or Email"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="org-instructor-search-input"
                fullWidth
              />
              {searching && <CircularProgress size={16} sx={{ mr: 1 }} />}
              {searchQuery && (
                <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ mr: 1 }}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Box className="org-instructor-control-buttons">
              <IconButton className="org-instructor-filter-button" onClick={handleOpenFilters}>
                <img src={FilterListIcon} alt="Filter" />
              </IconButton>
              <Button
                className="org-instructor-add-button"
                onClick={handleAddInstructor}
                variant="contained"
              >
                + Add
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Main Content with Filters */}
      <Box className="org-instructor-content-section">
        {/* Filters Panel */}
        {showFilters && (
          <Box ref={filterPanelRef} className="org-instructor-filters-panel">
            <Box className="org-instructor-filters-header">
              <Typography className="org-instructor-filters-title">Filters</Typography>
            </Box>

            <Box className="org-instructor-filters-content">
              <Box className="org-instructor-filters-grid">
                <FormControl className="org-instructor-filter-form-control">
                  <Typography className="org-instructor-filter-label">Instructor Type</Typography>
                  <Select
                    value={pendingInstructorTypeFilter}
                    onChange={e => handlePendingFilterChange('instructorType', e.target.value)}
                    className="org-instructor-filter-select"
                    displayEmpty
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          width: 200,
                        },
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    }}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Academic">Academic</MenuItem>
                    <MenuItem value="Creative">Creative</MenuItem>
                  </Select>
                </FormControl>

                <FormControl className="org-instructor-filter-form-control">
                  <Typography className="org-instructor-filter-label">Country</Typography>
                  <Select
                    value={pendingCountryFilter}
                    onChange={e => handlePendingFilterChange('country', e.target.value)}
                    className="org-instructor-filter-select"
                    displayEmpty
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          width: 200,
                        },
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    }}
                  >
                    <MenuItem value="All">All</MenuItem>
                    {countriesLoading && <MenuItem disabled>Loading...</MenuItem>}
                    {!countriesLoading &&
                      countryList.map(country => (
                        <MenuItem key={country.id} value={country.name}>
                          {country.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                {/* <FormControl className="org-instructor-filter-form-control">
                  <Typography className="org-instructor-filter-label">Gender</Typography>
                  <Select
                    value={pendingGenderFilter}
                    onChange={e => handlePendingFilterChange('gender', e.target.value)}
                    className="org-instructor-filter-select"
                    displayEmpty
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                  </Select>
                </FormControl> */}
              </Box>
            </Box>

            <Box className="org-instructor-filters-footer">
              <Button className="org-instructor-clear-all-button" onClick={clearAllPendingFilters}>
                Clear All
              </Button>
              <Button
                className="org-instructor-apply-button"
                variant="contained"
                onClick={handleApplyFilters}
              >
                Apply
              </Button>
            </Box>
          </Box>
        )}

        {/* Table Section */}
        <Box className="org-instructor-table-section">
          {loading ? (
            <Box className="instructor-table-scroll-container">
              <Box className="instructor-table-wrapper">
                <table className="instructor-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Instructor Type</th>
                      <th>Country</th>
                      <th>Subjects</th>
                      <th>Type of session</th>
                      <th>Total Sessions</th>
                      <th>Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 8 }).map((_, index) => (
                      <tr key={index}>
                        <td>
                          <Box className="instructor-avatar-name-container">
                            <div className="org-ins-skeleton org-ins-skeleton-avatar" />
                            <div
                              className="org-ins-skeleton org-ins-skeleton-line-sm"
                              style={{ marginLeft: 8 }}
                            />
                          </Box>
                        </td>
                        <td>
                          <div className="org-ins-skeleton org-ins-skeleton-badge" />
                        </td>
                        <td>
                          <div className="org-ins-skeleton org-ins-skeleton-line-sm" />
                        </td>
                        <td>
                          <div className="org-ins-skeleton org-ins-skeleton-line-md" />
                        </td>
                        <td>
                          <Box className="instructor-session-type-icons-style">
                            <div className="org-ins-skeleton org-ins-skeleton-icon" />
                            <div className="org-ins-skeleton org-ins-skeleton-icon" />
                            <div className="org-ins-skeleton org-ins-skeleton-icon" />
                          </Box>
                        </td>
                        <td>
                          <div className="org-ins-skeleton org-ins-skeleton-line-xs" />
                        </td>
                        <td>
                          <div className="org-ins-skeleton org-ins-skeleton-line-xs" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Box>
          ) : (
            <>
              {(!instructors || instructors.length === 0) &&
              // Only show the "Add Instructors" empty state when there is no search or filters applied
              searchQuery.trim() === '' &&
              instructorTypeFilter === 'All' &&
              countryFilter === 'All' &&
              genderFilter === 'All' ? (
                <Box className="org-instructor-empty-add-container">
                  <img
                    src={InstructorIcon}
                    alt="Add Instructors"
                    className="org-instructor-empty-add-icon"
                  />
                  <Typography className="org-instructor-empty-add-title">
                    Add Instructors
                  </Typography>
                  <Typography className="org-instructor-empty-add-desc">
                    Search and add existing teachers from the list to your organization
                    <br />
                    for streamlined management.
                  </Typography>
                  <Button
                    className="org-instructor-empty-add-btn"
                    variant="contained"
                    onClick={handleAddInstructor}
                  >
                    + Add New
                  </Button>
                </Box>
              ) : (
                <InstructorTable
                  instructorList={instructors}
                  // Removed selectedIds, onSelectOne, onSelectAll, onRemoveSelected props
                  instructorTypeFilter={instructorTypeFilter}
                  countryFilter={countryFilter}
                  genderFilter={genderFilter}
                  searchQuery={searchQuery}
                  org_id={organizationId}
                  onTotalSessionsSort={handleTotalSessionsSort}
                  onRatingSort={handleRatingSort}
                  totalCount={totalCount}
                />
              )}

              {/* Pagination */}
              {instructors && instructors.length > 0 && (
                <Box
                  className="org-ins-pagination-container"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 0,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <Typography className="org-ins-pagination-text">Showing</Typography>
                    <Select
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                      className="org-ins-pagination-select"
                      sx={{
                        height: '30px',
                        minWidth: '20px',
                        '& .MuiSelect-select': {
                          padding: '6px 4px 4px 8px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                      }}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={15}>15</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={30}>30</MenuItem>
                    </Select>
                    <Typography className="org-ins-pagination-text">
                      of {totalCount} Instructors
                    </Typography>
                    {loading && (
                      <Typography className="org-ins-pagination-text" sx={{ marginLeft: 1 }}>
                        Updating...
                      </Typography>
                    )}
                  </Box>
                  <nav>
                    <ul
                      style={{
                        listStyle: 'none',
                        padding: 0,
                        margin: '10px 0',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      {paginationItems.map(
                        ({ page: itemPage, type, selected, disabled, ...item }, index) => {
                          let children = null;
                          if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                            children = <span className="org-ins-pagination-ellipsis">…</span>;
                          } else if (type === 'page') {
                            children = (
                              <button
                                type="button"
                                className="org-ins-pagination-button"
                                style={{
                                  fontWeight: selected ? 'bold' : undefined,
                                  margin: '0 5px',
                                  padding: '5px 10px',
                                  cursor: 'pointer',
                                  backgroundColor: selected ? '#f0f0f0' : 'transparent',
                                  border: 'none',
                                  borderRadius: '4px',
                                }}
                                {...item}
                              >
                                {itemPage}
                              </button>
                            );
                          } else {
                            children = (
                              <button
                                type="button"
                                className="org-ins-pagination-button"
                                style={{
                                  margin: '0 5px',
                                  padding: '5px 10px',
                                  cursor: 'pointer',
                                  border: 'none',
                                  backgroundColor: 'transparent',
                                }}
                                disabled={disabled}
                                {...item}
                              >
                                {type === 'previous' ? 'Previous' : 'Next'}
                              </button>
                            );
                          }
                          return <li key={index}>{children}</li>;
                        }
                      )}
                    </ul>
                  </nav>
                </Box>
              )}
            </>
          )}
        </Box>
        {/* End Table Section */}
      </Box>
      {/* End Content Section */}

      {/* Add Instructor Modal Overlay */}
      {showAddInstructorModal && (
        <Box
          className="org-instructor-modal-overlay"
          onClick={e => {
            if (e.target === e.currentTarget) {
              handleCloseAddInstructorModal();
            }
          }}
        >
          <AddInstructorModal
            organizationId={organizationId}
            onClose={handleCloseAddInstructorModal}
            onSuccess={() => {
              handleCloseAddInstructorModal();
              fetchOrganizationInstructors();
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default OrganizationInstructor;
